import { useState, useEffect, useContext, createContext, useRef } from 'react';
import supabase from '../utils/supabaseClient';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userSystems, setUserSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Refs para controle de estado e debouncing
  const loadingUserDataRef = useRef(false);
  const lastLoadTimeRef = useRef(0);
  const loadTimeoutRef = useRef(null);

  // Carregar dados do usuário com debouncing e controle de concorrência
  const loadUserData = async (userId, force = false) => {
    try {
      // Evitar múltiplas chamadas simultâneas
      if (loadingUserDataRef.current && !force) {
        console.log('📊 Carregamento já em andamento, ignorando...');
        return;
      }

      // Debouncing - evitar chamadas muito frequentes
      const now = Date.now();
      if (now - lastLoadTimeRef.current < 2000 && !force) {
        console.log('📊 Carregamento muito recente, ignorando...');
        return;
      }

      loadingUserDataRef.current = true;
      lastLoadTimeRef.current = now;
      
      console.log('📊 Carregando dados do usuário:', userId);
      
      // Carregar perfil
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        console.warn('⚠️ Erro ao carregar perfil:', profileError);
        setUserProfile(null);
      } else {
        console.log('✅ Perfil carregado');
        setUserProfile(profile);
      }

      // Carregar sistemas do usuário
      const { data: userAccess, error: accessError } = await supabase
        .from('user_system_access')
        .select(`
          *,
          systems:system_id (
            id, name, description, slug, icon, color, route, active
          ),
          system_roles:role_id (
            id, name, description, permissions, active
          )
        `)
        .eq('profile_id', userId)
        .eq('active', true);

      if (accessError) {
        console.warn('⚠️ Erro ao carregar sistemas:', accessError);
        setUserSystems([]);
        setIsAdmin(false);
      } else {
        const activeSystems = userAccess?.filter(access => 
          access.systems?.active && access.system_roles?.active
        ) || [];

        console.log('✅ Sistemas carregados:', activeSystems.length);
        setUserSystems(activeSystems);

        // Verificar se é admin
        const hasAdminAccess = activeSystems.some(access => 
          access.systems?.slug === 'admin-hub'
        );
        setIsAdmin(hasAdminAccess);
      }

    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
      setUserProfile(null);
      setUserSystems([]);
      setIsAdmin(false);
    } finally {
      loadingUserDataRef.current = false;
    }
  };

  // Login simplificado
  const login = async (email, password) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      
      return data;
    } catch (error) {
      console.error('❌ Erro no login:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Logout simplificado
  const logout = async () => {
    try {
      console.log('🚪 Fazendo logout...');
      
      // Limpar dados primeiro
      setUser(null);
      setUserProfile(null);
      setUserSystems([]);
      setIsAdmin(false);
      
      // Limpar refs
      loadingUserDataRef.current = false;
      lastLoadTimeRef.current = 0;
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
      
      await supabase.auth.signOut();
      
    } catch (error) {
      console.error('❌ Erro no logout:', error);
    }
  };

  // Verificar sessão inicial e configurar listeners
  useEffect(() => {
    let mounted = true;
    let authSubscription = null;

    const initAuth = async () => {
      try {
        console.log('🔄 Inicializando autenticação...');
        
        // Verificar sessão atual
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!mounted) return;

        if (session?.user) {
          console.log('👤 Sessão encontrada');
          setUser(session.user);
          await loadUserData(session.user.id, true); // Force na inicialização
        } else {
          console.log('🔍 Nenhuma sessão encontrada');
        }
      } catch (error) {
        console.error('❌ Erro na inicialização:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    // Configurar listener de mudanças de auth com melhor controle
    const setupAuthListener = () => {
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          if (!mounted) return;

          console.log('🔐 Auth mudou:', event, session?.user?.id);
          
          // Limpar timeout anterior se existir
          if (loadTimeoutRef.current) {
            clearTimeout(loadTimeoutRef.current);
          }
          
          if (event === 'SIGNED_IN' && session?.user) {
            // Se já temos um usuário com o mesmo ID, não recarregar desnecessariamente
            if (user && user.id === session.user.id) {
              console.log('👤 Usuário já carregado, ignorando SIGNED_IN duplicado');
              return;
            }
            
            setUser(session.user);
            
            // Debounce para evitar múltiplas chamadas rápidas
            loadTimeoutRef.current = setTimeout(() => {
              if (mounted) {
                loadUserData(session.user.id);
              }
            }, 500);
            
          } else if (event === 'SIGNED_OUT') {
            console.log('🚪 Usuário deslogado via listener');
            setUser(null);
            setUserProfile(null);
            setUserSystems([]);
            setIsAdmin(false);
            
            // Limpar refs
            loadingUserDataRef.current = false;
            lastLoadTimeRef.current = 0;
          } else if (event === 'TOKEN_REFRESHED' && session?.user) {
            console.log('🔄 Token renovado');
            // Token refreshed - não precisa recarregar dados
            setUser(session.user);
          }
          
          setLoading(false);
        }
      );
      
      authSubscription = subscription;
    };

    initAuth();
    setupAuthListener();

    // Timeout de segurança
    const timeout = setTimeout(() => {
      if (mounted && loading) {
        console.warn('⏰ Timeout de loading atingido');
        setLoading(false);
      }
    }, 10000);

    // Event listener para quando a página fica visível novamente
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        console.log('👁️ Página visível novamente');
        // Não recarregar automaticamente - só se necessário
        // loadUserData(user.id);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mounted = false;
      if (authSubscription) {
        authSubscription.unsubscribe();
      }
      clearTimeout(timeout);
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []); // Removido 'user' das dependências para evitar loops

  // Funções utilitárias
  const hasSystemAccess = (systemSlug) => {
    return userSystems.some(access => access.systems?.slug === systemSlug);
  };

  const hasSystemPermission = (systemSlug, permissionName) => {
    const systemAccess = userSystems.find(access => access.systems?.slug === systemSlug);
    const permissions = systemAccess?.system_roles?.permissions || {};
    return permissions[permissionName] === true;
  };

  const forceReload = () => {
    if (user) {
      console.log('🔄 Forçando recarga dos dados...');
      loadUserData(user.id, true);
    }
  };

  const value = {
    user,
    userProfile,
    userSystems,
    loading,
    isAdmin,
    hasSystemAccess,
    hasSystemPermission,
    login,
    logout,
    forceReload, // Nova função para forçar recarga
    loadUserData: () => user && loadUserData(user.id, true)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};